import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import {
  mergeStatusOptionsWithDefaults,
  normalizeFieldOptions,
} from "../../../utils/taskTemplateChipStyles";
import { normalizeTaskListRows } from "../../../utils/taskDeadlineDisplay";
import { sortTaskTemplateTableRows } from "../../../utils/taskTableRowSort";

const normalizeTemplateFieldOptionsPayload = (payload) => {
  const data = payload || {};
  return {
    typeOptions: normalizeFieldOptions(data.typeOptions, "type"),
    statusOptions: mergeStatusOptionsWithDefaults(data.statusOptions),
    priorityOptions: normalizeFieldOptions(data.priorityOptions, "priority"),
  };
};

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const authHeaders = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error("No token found");
  return { Authorization: `Bearer ${token}` };
};

const TASK_TEMPLATES_LIST_LIMIT = 25;

export const fetchTaskTemplates = createAsyncThunk(
  "taskTemplates/fetchList",
  async (
    { page = 1, limit = TASK_TEMPLATES_LIST_LIMIT, search = "", status = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const headers = await authHeaders();
      const params = { page, limit, search };
      if (status && status !== "all") params.status = status;
      const response = await axios.get(`${SERVER_DOMAIN}/api/task-templates`, {
        headers,
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Broader, tab-agnostic template list for cross-tab pickers ("Create from Template" on
 * the Tasks tab, template role suggestions) — independent of the Templates tab's own
 * paginated/filtered/search table state, so those pickers work without requiring the
 * user to have visited the Templates tab first. Fetched once on mount, not per-tab.
 * `limit: 500` is the backend's max allowed page size — shows every template the
 * picker should offer (matches the account-wide list the picker used pre-refactor).
 */
export const fetchTaskTemplatesForPicker = createAsyncThunk(
  "taskTemplates/fetchForPicker",
  async (_, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/task-templates`, {
        headers,
        params: { page: 1, limit: 500, search: "", status: "all" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTaskTemplateStatusCounts = createAsyncThunk(
  "taskTemplates/fetchStatusCounts",
  async (_, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/task-templates/status-counts`, {
        headers,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTaskTemplateById = createAsyncThunk(
  "taskTemplates/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/task-templates/${id}`, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTaskTemplate = createAsyncThunk(
  "taskTemplates/create",
  async (payload, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.post(`${SERVER_DOMAIN}/api/task-templates`, payload, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTaskTemplate = createAsyncThunk(
  "taskTemplates/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(`${SERVER_DOMAIN}/api/task-templates/${id}`, payload, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const patchTaskTemplateStatus = createAsyncThunk(
  "taskTemplates/patchStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/task-templates/${id}/status`,
        { status },
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const patchTaskTemplatePriority = createAsyncThunk(
  "taskTemplates/patchPriority",
  async ({ id, priority }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/task-templates/${id}/priority`,
        { priority },
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const patchTaskTemplateType = createAsyncThunk(
  "taskTemplates/patchType",
  async ({ id, templateType }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/task-templates/${id}/type`,
        { templateType },
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteTaskTemplate = createAsyncThunk(
  "taskTemplates/delete",
  async (id, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      await axios.delete(`${SERVER_DOMAIN}/api/task-templates/${id}`, { headers });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTaskTemplateFieldOptions = createAsyncThunk(
  "taskTemplates/fetchFieldOptions",
  async (_, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/task-templates/field-options`, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTaskTemplateFieldOptions = createAsyncThunk(
  "taskTemplates/updateFieldOptions",
  async (payload, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.put(`${SERVER_DOMAIN}/api/task-templates/field-options`, payload, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const uploadTaskTemplateFile = createAsyncThunk(
  "taskTemplates/uploadFile",
  async (file, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${SERVER_DOMAIN}/api/task-templates/upload/file`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const mergeTemplateRowFromListItem = (state, row) => {
  if (!row) return;
  const rowId = String(row.id || row._id || "");
  if (!rowId) return;
  const patch = row.id ? row : { ...row, id: rowId };
  const idx = state.rows.findIndex((r) => String(r.id) === rowId);
  if (idx !== -1) {
    state.rows[idx] = { ...state.rows[idx], ...patch };
  }
  state.rows = sortTaskTemplateTableRows(state.rows);

  const pickerIdx = state.pickerRows.findIndex((r) => String(r.id) === rowId);
  if (pickerIdx !== -1) {
    state.pickerRows[pickerIdx] = { ...state.pickerRows[pickerIdx], ...patch };
  }
};

const initialState = {
  rows: [],
  pagination: { page: 1, limit: TASK_TEMPLATES_LIST_LIMIT, total: 0 },
  statusCounts: { all: 0, pending: 0, inProgress: 0, cancelled: 0, completed: 0 },
  statusCountsLoading: false,
  /** Cross-tab picker rows (Create-from-Template on Tasks tab, role suggestions) — independent of `rows`/`pagination` above. */
  pickerRows: [],
  pickerLoading: false,
  loading: false,
  error: null,
  selectedTemplate: null,
  selectedLoading: false,
  uploadLoading: false,
  saving: false,
  deleting: false,
  patching: false,
  fieldOptions: { typeOptions: [], statusOptions: [], priorityOptions: [] },
  fieldOptionsLoading: false,
  fieldOptionsSaving: false,
};

const taskTemplatesSlice = createSlice({
  name: "taskTemplates",
  initialState,
  reducers: {
    clearTaskTemplateError(state) {
      state.error = null;
    },
    clearSelectedTaskTemplate(state) {
      state.selectedTemplate = null;
    },
    setTemplatesPage(state, action) {
      state.pagination.page = action.payload;
    },
    setTemplatesLimit(state, action) {
      state.pagination.limit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskTemplatesForPicker.pending, (state) => {
        state.pickerLoading = true;
      })
      .addCase(fetchTaskTemplatesForPicker.fulfilled, (state, action) => {
        state.pickerLoading = false;
        state.pickerRows = sortTaskTemplateTableRows(normalizeTaskListRows(action.payload.data || []));
      })
      .addCase(fetchTaskTemplatesForPicker.rejected, (state) => {
        state.pickerLoading = false;
      })
      .addCase(fetchTaskTemplateStatusCounts.pending, (state) => {
        state.statusCountsLoading = true;
      })
      .addCase(fetchTaskTemplateStatusCounts.fulfilled, (state, action) => {
        state.statusCountsLoading = false;
        state.statusCounts = action.payload || state.statusCounts;
      })
      .addCase(fetchTaskTemplateStatusCounts.rejected, (state) => {
        state.statusCountsLoading = false;
      })
      .addCase(fetchTaskTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = sortTaskTemplateTableRows(normalizeTaskListRows(action.payload.data || []));
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchTaskTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTaskTemplateById.pending, (state) => {
        state.selectedLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskTemplateById.fulfilled, (state, action) => {
        state.selectedLoading = false;
        state.selectedTemplate = action.payload;
      })
      .addCase(fetchTaskTemplateById.rejected, (state, action) => {
        state.selectedLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadTaskTemplateFile.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadTaskTemplateFile.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadTaskTemplateFile.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload;
      })
      .addCase(createTaskTemplate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createTaskTemplate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createTaskTemplate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateTaskTemplate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateTaskTemplate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateTaskTemplate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(patchTaskTemplateStatus.pending, (state) => {
        state.patching = true;
      })
      .addCase(patchTaskTemplateStatus.fulfilled, (state, action) => {
        state.patching = false;
        mergeTemplateRowFromListItem(state, action.payload);
      })
      .addCase(patchTaskTemplateStatus.rejected, (state, action) => {
        state.patching = false;
        state.error = action.payload;
      })
      .addCase(patchTaskTemplatePriority.pending, (state) => {
        state.patching = true;
      })
      .addCase(patchTaskTemplatePriority.fulfilled, (state, action) => {
        state.patching = false;
        mergeTemplateRowFromListItem(state, action.payload);
      })
      .addCase(patchTaskTemplatePriority.rejected, (state, action) => {
        state.patching = false;
        state.error = action.payload;
      })
      .addCase(patchTaskTemplateType.pending, (state) => {
        state.patching = true;
      })
      .addCase(patchTaskTemplateType.fulfilled, (state, action) => {
        state.patching = false;
        mergeTemplateRowFromListItem(state, action.payload);
      })
      .addCase(patchTaskTemplateType.rejected, (state, action) => {
        state.patching = false;
        state.error = action.payload;
      })
      .addCase(deleteTaskTemplate.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteTaskTemplate.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteTaskTemplate.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      })
      .addCase(fetchTaskTemplateFieldOptions.pending, (state) => {
        state.fieldOptionsLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskTemplateFieldOptions.fulfilled, (state, action) => {
        state.fieldOptionsLoading = false;
        state.fieldOptions = normalizeTemplateFieldOptionsPayload(action.payload);
      })
      .addCase(fetchTaskTemplateFieldOptions.rejected, (state, action) => {
        state.fieldOptionsLoading = false;
        state.error = action.payload;
      })
      .addCase(updateTaskTemplateFieldOptions.pending, (state) => {
        state.fieldOptionsSaving = true;
        state.error = null;
      })
      .addCase(updateTaskTemplateFieldOptions.fulfilled, (state, action) => {
        state.fieldOptionsSaving = false;
        state.fieldOptions = normalizeTemplateFieldOptionsPayload(action.payload);
      })
      .addCase(updateTaskTemplateFieldOptions.rejected, (state, action) => {
        state.fieldOptionsSaving = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearTaskTemplateError,
  clearSelectedTaskTemplate,
  setTemplatesPage,
  setTemplatesLimit,
} = taskTemplatesSlice.actions;
export default taskTemplatesSlice.reducer;
